/**
 * Collector numbers: the one rule for whether a typed query is the number printed on a card (#661).
 *
 * A collector number is the card's position within its set as printed on the card face. It is not
 * an integer: besides a plain `4` or `070`, subsets and promos print letter-prefixed numbers
 * (Trainer Gallery `TG12`, Galarian Gallery `GG05`, Shiny Vault `SV107`, Sword & Shield promos
 * `SWSH050`, Aquapolis `H12`), and vendors type them as printed. A number is often printed over its
 * set size (`5/62`, `TG12/TG30`), and a trailing letter distinguishes two cards sharing a number
 * (`70a` is not `70`).
 *
 * Every client-side card filter (inventory list, labels picker, StockGroup picker, customer
 * storefront) calls this module, so each answers a typed number, or a name followed by its number,
 * the same way. The catalog set-browse grid is paged by the server (TRO-595), which applies the
 * backend's copy of this rule. This module mirrors the backend's
 * `trovie.domain.tcg.collector_number`, and both suites execute the parse/match cases in
 * `shared/fixtures/collector-number.json`, so a change on one side only turns the other suite red
 * (#775). Put a new shared case in that table.
 */

/** One printed collector number, split so equal cards compare equal. */
export type CollectorNumber = {
  /** Lowercased letters printed before the digits; `""` for a plain number. */
  readonly prefix: string;
  /** The digits with leading zeros stripped; never empty. */
  readonly digits: string;
  /** The lowercased single letter printed after the digits; `""` when there is none. */
  readonly suffix: string;
  /** The leading-zero-insensitive comparison key (`070` and `70` both → `70`). */
  readonly canonical: string;
};

// An optional letter prefix, the digits, and an optional single-letter suffix. The prefix is capped
// at 4 letters because real subset codes are 1-4 letters ("H", "TG", "RC", "SWSH") while card names
// are longer, so a name like "Porygon2" does not read as a collector number. `[0-9]` rather than
// `\d` keeps this identical to the backend rule, where `\d` would also match Unicode digits.
const TOKEN = "([a-z]{0,4})([0-9]{1,5})([a-z]?)";

// A typed query is a bare collector number only when the whole query is one: an optional "#", the
// number, and an optional printed "/<set size>" tail ("5/62", "TG12/TG30", "001/SM-P").
const QUERY_RE = new RegExp(
  `^[ \\t]*#?[ \\t]*${TOKEN}(?:[ \\t]*/[ \\t]*[a-z0-9-]{1,10})?[ \\t]*$`,
  "i",
);

// A stored field value is read leniently from its leading token, so a catalog's "70/102" or
// "TG12/TG30" reduces to the number without the denominator having to be well-formed.
const FIELD_RE = new RegExp(`^[ \\t]*#?[ \\t]*${TOKEN}`, "i");

function build(m: RegExpExecArray): CollectorNumber | null {
  const prefix = m[1].toLowerCase();
  const suffix = m[3].toLowerCase();
  const digits = m[2].replace(/^0+/, "");
  if (digits === "") return null; // No card is numbered zero ("0", "000", "TG00").
  return { prefix, digits, suffix, canonical: `${prefix}${digits}${suffix}` };
}

/**
 * Parses a typed query that is a bare collector number; returns `null` for a name query.
 * `"70"`, `"070"`, `"#83"`, `"5/62"`, `"70a"`, `"TG12"`, `"SWSH050"` are numbers;
 * `"charizard"`, `"mega lucario ex"`, `"70 charizard"` are not.
 */
export function parseCollectorNumber(query: string | null | undefined): CollectorNumber | null {
  const m = QUERY_RE.exec(query ?? "");
  return m ? build(m) : null;
}

/**
 * Parses the number a stored field carries from its leading token, so a printed denominator
 * (`"70/102"`, `"TG12/TG30"`, `"001/SM-P"`) does not have to be well-formed.
 */
export function parsePrintedNumber(field: string | null | undefined): CollectorNumber | null {
  const m = FIELD_RE.exec(field ?? "");
  return m ? build(m) : null;
}

/** A card as a text filter sees it: whatever name and printed number the surface carries. */
export type CardText = { name?: string | null; number?: string | null; };

/**
 * One typed query, parsed once for a whole list.
 *
 * The rule every surface shares: a bare number widens the name search, while a number after a name
 * narrows both fields together (`Mew 151`). Collector-number matching is whole-number, so `70`
 * finds #70 / #070 / #70/102 but never #170, #700, or the distinct card #70a. The full query also
 * remains a name match so a real card name ending in digits survives. An empty query is not a
 * constraint and matches everything.
 */
export type CardQuery = {
  /** The trimmed, lowercased text — `""` when nothing was typed. */
  readonly text: string;
  /** The name before a trailing collector number; `null` unless both were typed. */
  readonly namePart: string | null;
  /** The bare or trailing collector number; `null` when the query carries none. */
  readonly number: CollectorNumber | null;
  /**
   * Whether the query spells out a collector number rather than reading as a name. It has no
   * client caller: it and `matchesText` are the two halves of the server's `matches_in_set`, the
   * set-browse rule the backend owns (TRO-595). They stay mirrored here so the shared fixture keeps
   * both sides one rule.
   */

  readonly isNumber: boolean;
  /** Apply the shared name/number rule. A blank query matches everything. */
  matches(card: CardText): boolean;
  /** Case-insensitive substring against any other searchable text a surface carries. */
  matchesText(text: string | null | undefined): boolean;
};

/** Parse a typed query once, then test cards against it. */
export function cardQuery(query: string): CardQuery {
  const text = query.trim().toLowerCase();
  let number = parseCollectorNumber(text);
  let namePart: string | null = null;
  if (number === null) {
    const parts = text.split(/[ \t]+/);
    for (let index = 1; index < parts.length; index += 1) {
      const trailing = parseCollectorNumber(parts.slice(index).join(" "));
      if (trailing !== null) {
        namePart = parts.slice(0, index).join(" ");
        number = trailing;
        break;
      }
    }
  }
  const matchesText = (t: string | null | undefined) =>
    text === "" || (t ?? "").toLowerCase().includes(text);
  const matchesNumber = (n: string | null | undefined) => {
    if (number === null) return false;
    const printed = parsePrintedNumber(n);
    return printed !== null && printed.canonical === number.canonical;
  };
  return {
    text,
    namePart,
    number,
    isNumber: number !== null && namePart === null,
    matchesText,
    matches: (card) =>
      text === "" || matchesText(card.name)
      || (matchesNumber(card.number)
        && (namePart === null || (card.name ?? "").toLowerCase().includes(namePart))),
  };
}
